import React, { useEffect, useState } from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Radio,
  RadioGroup,
  FormControl,
  FormLabel,
  Select,
  MenuItem,
  InputLabel
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import { Button, TextField, TextView } from '@atoms';
import { toast } from 'react-toastify';
import { useUpdateArtifactPassword, materialTheme } from '@particles';

type PasswordMode = 'set' | 'remove' | 'keep';
type AccessLevel = 'public' | 'client' | 'internal';

interface PasswordArtifactDialogProps {
  open: boolean;
  artifact: Artifact | null;
  onClose: () => void;
}

const PasswordArtifactDialog: React.FC<PasswordArtifactDialogProps> = ({ open, artifact, onClose }) => {
  const [mode, setMode] = useState<PasswordMode>('keep');
  const [password, setPassword] = useState('');
  const [accessLevel, setAccessLevel] = useState<AccessLevel>('client');
  const initialAccessLevel: AccessLevel = (artifact?.accessLevel as AccessLevel) || 'client';

  const mutation = useUpdateArtifactPassword();

  useEffect(() => {
    if (open) {
      setMode('keep');
      setPassword('');
      setAccessLevel((artifact?.accessLevel as AccessLevel) || 'client');
    }
  }, [open, artifact]);

  const handleClose = () => {
    if (mutation.isPending) return;
    onClose();
  };

  const handleSubmit = () => {
    if (!artifact) return;
    if (mode === 'set' && !password.trim()) {
      toast.error('Enter a password or pick a different action');
      return;
    }
    const accessChanged = accessLevel !== initialAccessLevel;
    if (mode === 'keep' && !accessChanged) {
      toast.info('Nothing to update');
      return;
    }

    const vars: {
      id: string;
      password?: string;
      removePassword?: boolean;
      accessLevel?: AccessLevel;
    } = { id: artifact.id };
    if (mode === 'set') vars.password = password;
    else if (mode === 'remove') vars.removePassword = true;
    if (accessChanged) vars.accessLevel = accessLevel;

    mutation.mutate(vars, {
      onSuccess: () => {
        const parts: string[] = [];
        if (mode === 'set') parts.push('Password updated');
        else if (mode === 'remove') parts.push('Password removed');
        if (accessChanged) parts.push(`Access level set to ${accessLevel}`);
        toast.success(parts.join(' · ') || 'Updated');
        onClose();
      },
      onError: (error: unknown) => {
        const errorMessage = error && typeof error === 'object' && 'error' in error
          ? String((error as { error: unknown }).error)
          : 'Unknown error';
        toast.error(`Failed to update: ${errorMessage}`);
      }
    });
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <LockIcon sx={{ mr: 1, color: materialTheme.palette.primary.main }} />
          <TextView variant="h6" $fontWeight={600}>
            Password protection
          </TextView>
        </Box>
        <TextView variant="caption" color="textSecondary">
          {artifact ? `Share link: /a/${artifact.fileName}` : ''}
        </TextView>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ p: 2, bgcolor: '#f8f9fa', borderRadius: 1, mb: 2 }}>
          <FormControl fullWidth sx={{ mb: 1 }}>
            <InputLabel id="access-level-label">Access level</InputLabel>
            <Select
              labelId="access-level-label"
              value={accessLevel}
              label="Access level"
              onChange={(e) => setAccessLevel(e.target.value as AccessLevel)}
              size="small">
              <MenuItem value="public">Public — no gate, anyone with the link</MenuItem>
              <MenuItem value="client">Client — opens with client master or per-artifact password</MenuItem>
              <MenuItem value="internal">Internal — opens with internal key or per-artifact password</MenuItem>
            </Select>
          </FormControl>
          <TextView variant="caption" color="textSecondary">
            The system master keys always unlock at their level; a per-artifact password (below) scopes access to that one report.
          </TextView>
        </Box>
        <Box sx={{ p: 2, bgcolor: '#f8f9fa', borderRadius: 1 }}>
          <FormControl>
            <FormLabel sx={{ fontWeight: 600, mb: 1 }}>Per-artifact password</FormLabel>
            <RadioGroup
              row
              value={mode}
              onChange={(e) => {
                const v = e.target.value as PasswordMode;
                setMode(v);
                if (v !== 'set') setPassword('');
              }}>
              <FormControlLabel value="keep" control={<Radio />} label="Leave unchanged" />
              <FormControlLabel value="set" control={<Radio />} label="Set / change" />
              <FormControlLabel value="remove" control={<Radio />} label="Remove" />
            </RadioGroup>
          </FormControl>
          {mode === 'set' && (
            <TextField
              label="New password"
              name="artifactPassword"
              type="password"
              value={password}
              fullWidth
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Viewers will need this password to open the link"
            />
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button variant="outlined" onClick={handleClose} disabled={mutation.isPending}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          loading={mutation.isPending}
          disabled={mutation.isPending || (mode === 'set' && !password.trim())}
          startIcon={<LockIcon />}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PasswordArtifactDialog;
